// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { prepareFiltersForServer } from "@/lib/benchmark-utils";
import type { CampaignMetrics } from "./use-campaign-metrics";

interface UseBenchmarkMetricsOptions {
  campaignId: string | null;
  metrics: Array<keyof CampaignMetrics>;
  benchmarkFilters: Record<string, unknown>;
  excludeCurrentCampaign?: boolean;
  enabled?: boolean;
  selectedCampaignIds?: string[]; // For static mode
  mode?: "static" | "dynamic"; // Add explicit mode parameter
}

export function useBenchmarkMetrics({
  campaignId,
  metrics,
  benchmarkFilters,
  excludeCurrentCampaign = true,
  enabled = true,
  selectedCampaignIds = [],
  mode = "static", // Default to static mode
}: UseBenchmarkMetricsOptions) {
  // Prepare filters for the server
  const preparedFilters = useMemo(
    () => prepareFiltersForServer(benchmarkFilters),
    [benchmarkFilters],
  );

  // Use stringified versions of filters to avoid unnecessary dependency changes
  const benchmarkFiltersString = JSON.stringify(preparedFilters);
  const metricFiltersString = benchmarkFilters.metric_filters
    ? JSON.stringify(benchmarkFilters.metric_filters)
    : null;

  // Build the URL for benchmark metrics
  const buildURL = useCallback(() => {
    const searchParams = new URLSearchParams();

    // Add requested metrics
    metrics.forEach((metric) => searchParams.append("metrics", metric));

    // Add benchmark flag
    searchParams.append("is_benchmark", "true");

    // Always add the mode parameter to explicitly indicate the intended mode
    searchParams.append("mode", mode);

    // Set the exclude_current_campaign parameter based on mode and configuration
    // In static mode, we never exclude the current campaign
    const shouldExcludeCurrent =
      mode === "static" ? false : excludeCurrentCampaign;
    searchParams.append(
      "exclude_current_campaign",
      shouldExcludeCurrent ? "true" : "false",
    );

    // Always include campaign_ids parameter for static mode
    if (mode === "static" && selectedCampaignIds.length > 0) {
      searchParams.append("campaign_ids", selectedCampaignIds.join(","));
    }

    // Always include benchmark_filters for dynamic mode or when in static mode with empty selection
    if (Object.keys(preparedFilters).length > 0) {
      searchParams.append("benchmark_filters", benchmarkFiltersString);
    }

    // Add metric_filters if they exist in the benchmark filters (to respect opportunity_types)
    if (metricFiltersString) {
      searchParams.append("filters", metricFiltersString);
    }

    return `/datahub/campaigns/${
      campaignId || "null"
    }/benchmark-metrics/?${searchParams.toString()}`;
  }, [
    campaignId,
    metrics,
    benchmarkFiltersString,
    excludeCurrentCampaign,
    metricFiltersString, // Use string instead of object reference
    selectedCampaignIds,
    mode, // Include mode in the dependency array
    preparedFilters,
  ]);

  // Generate the URL once
  const url = buildURL();

  // Execute query with the built URL

  // Execute the query with minimal options - only fetch when explicitly enabled
  const query = api.useQuery<CampaignMetrics>(url, {
    enabled:
      enabled &&
      !!campaignId &&
      // In static mode, we need at least one selected campaign
      (selectedCampaignIds.length > 0 ||
        // In dynamic mode, filter criteria are optional, we just need a valid URL
        selectedCampaignIds.length === 0),
    // Disable automatic refetching for better performance
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Override the default stale time to disable caching for benchmark metrics
    staleTime: 0, // Force refetch every time
    gcTime: 0, // Don't keep in cache at all
  });

  // Create loading states for all metrics
  const loadingStates = metrics.reduce(
    (acc, metric) => {
      acc[metric] = {
        isLoading: query.isLoading,
        isError: !!query.error,
        error: query.error,
      };
      return acc;
    },
    {} as Record<
      keyof CampaignMetrics,
      { isLoading: boolean; isError: boolean; error: unknown }
    >,
  );

  // Process query results (no additional transformation needed)

  return {
    data: query.data || {},
    isLoading: query.isLoading,
    isError: query.isError,
    loadingStates,
  };
}
